/**
 * Design fix 3 — an email source is a mailbox with mail-client settings (sources.md "email"): provider
 * presets, IMAP host, port, security, user, password, mailbox (picked from the server's folders after
 * "Test connection"), optional sender filter. Host, port, user and mailbox are the source's identity:
 * read-only once it exists. The password lives only in this component's state and goes to the engine
 * as a secret (SecretStore), never into the draft or a row.
 */

import { useState } from 'react';
import { createBridgeMailTransport } from '@digester/core';
import { useServices } from '../../app/services.js';
import { Icon } from '../../icons/Icon.js';
import { Field, FormGrid, Select } from '../../ui/form.js';
import { describeSourceError } from './errors.js';
import { MAIL_PRESETS } from './kinds.js';

export interface EmailConfigDraft {
  host: string;
  port: number;
  security: 'tls' | 'starttls' | 'none';
  user: string;
  mailbox: string;
  fromFilter: string;
}

export interface EmailFieldsProps {
  value: EmailConfigDraft;
  onChange(next: EmailConfigDraft): void;
  password: string;
  onPassword(password: string): void;
  /** The source exists: identity fields are read-only, the password field replaces the stored one. */
  existing: boolean;
  /** For an existing source with no typed password: test through the engine (it has the stored password). */
  onTestExisting?: () => Promise<{ ok: boolean; message: string }>;
}

const SECURITY: ReadonlyArray<readonly ['tls' | 'starttls' | 'none', string]> = [
  ['tls', 'SSL/TLS'],
  ['starttls', 'STARTTLS'],
  ['none', 'None'],
];

type TestState = { kind: 'idle' } | { kind: 'busy' } | { kind: 'ok'; mailboxes: string[] | null; message: string } | { kind: 'error'; message: string };

export function EmailFields({ value, onChange, password, onPassword, existing, onTestExisting }: EmailFieldsProps) {
  const { api } = useServices();
  const [preset, setPreset] = useState('custom');
  const [test, setTest] = useState<TestState>({ kind: 'idle' });
  const mailboxes = test.kind === 'ok' ? test.mailboxes : null;
  const note = MAIL_PRESETS.find((p) => p.id === preset)?.note;

  const choosePreset = (id: string): void => {
    setPreset(id);
    const p = MAIL_PRESETS.find((x) => x.id === id);
    if (p) onChange({ ...value, host: p.host, port: p.port, security: p.security });
  };

  const testConnection = async (): Promise<void> => {
    if (existing && password === '' && onTestExisting) {
      setTest({ kind: 'busy' });
      const r = await onTestExisting();
      setTest(r.ok ? { kind: 'ok', mailboxes: null, message: r.message } : { kind: 'error', message: r.message });
      return;
    }
    const form = document.getElementById('settings-form');
    if (form instanceof HTMLFormElement && !form.reportValidity()) return;
    setTest({ kind: 'busy' });
    try {
      const list = await createBridgeMailTransport(api).listMailboxes({ host: value.host.trim(), port: value.port, security: value.security, user: value.user.trim(), password });
      setTest({ kind: 'ok', mailboxes: list, message: `Connected. ${list.length} folder${list.length === 1 ? '' : 's'} on the server.` });
      if (!existing && list.length > 0 && !list.includes(value.mailbox)) onChange({ ...value, mailbox: list.includes('INBOX') ? 'INBOX' : (list[0] as string) });
    } catch (error) {
      setTest({ kind: 'error', message: describeSourceError(error, 'imap') });
    }
  };

  return (
    <div className="email-fields">
      {existing ? null : (
        <Select
          label="Provider"
          name="preset"
          value={preset}
          options={[['custom', 'Other mail server'] as const, ...MAIL_PRESETS.map((p) => [p.id, p.name] as const)]}
          onChange={choosePreset}
        />
      )}
      {note ? <p className="field-help email-preset-note">{note}</p> : null}
      <FormGrid>
        <Field label="IMAP server" name="host" value={value.host} required readOnly={existing} placeholder="imap.example.com" autoComplete="off" onChange={(host) => onChange({ ...value, host })} />
        <Field label="Port" name="port" type="number" min={1} max={65535} required readOnly={existing} value={value.port} onChange={(port) => onChange({ ...value, port: Number(port) })} />
      </FormGrid>
      <FormGrid>
        <Select
          label="Security"
          name="security"
          value={value.security}
          options={SECURITY}
          onChange={(security) => onChange({ ...value, security, ...(existing ? {} : { port: security === 'starttls' ? 143 : security === 'tls' ? 993 : value.port }) })}
        />
        <Field label="User name" name="user" value={value.user} required readOnly={existing} placeholder="you@example.com" autoComplete="off" onChange={(user) => onChange({ ...value, user })} />
      </FormGrid>
      <Field
        label={existing ? 'Password · replace' : 'Password'}
        name="password"
        type="password"
        autoComplete="new-password"
        required={!existing}
        placeholder={existing ? 'Leave empty to keep the password on this device' : 'An app password where the provider offers one'}
        value={password}
        onChange={onPassword}
      />
      {mailboxes && mailboxes.length > 0 && !existing ? (
        <Select label="Mailbox" name="mailbox" value={value.mailbox} options={mailboxes.map((m) => [m, m] as const)} onChange={(mailbox) => onChange({ ...value, mailbox })} />
      ) : (
        <Field label="Mailbox" name="mailbox" value={value.mailbox} required readOnly={existing} onChange={(mailbox) => onChange({ ...value, mailbox })} help={existing ? undefined : 'Test the connection to pick from the server’s folders.'} />
      )}
      <Field
        label="Only mail from · optional"
        name="fromFilter"
        value={value.fromFilter}
        placeholder="newsletter@example.com, digest@"
        onChange={(fromFilter) => onChange({ ...value, fromFilter })}
        help="Comma-separated. Mail whose sender contains none of these is skipped."
      />
      <div className="email-test">
        <button type="button" className="secondary-button" data-action="test-connection" disabled={test.kind === 'busy'} onClick={() => void testConnection()}>
          <Icon name="plug" /> Test connection
        </button>
        <span>Reads the folder list; nothing is changed on the server.</span>
      </div>
      {test.kind === 'ok' ? (
        <div className="test-summary" id="connection-result">
          {test.message}
        </div>
      ) : test.kind === 'error' ? (
        <div className="test-error" id="connection-result" role="alert">
          <Icon name="circle-alert" />
          <span>{test.message}</span>
        </div>
      ) : null}
      <p className="field-help">
        Your password travels through Digester’s mail bridge for each check and is never stored there. It is kept encrypted on this device only; other devices need it entered
        once. Digester only reads: it never marks, moves or deletes mail.
      </p>
    </div>
  );
}
